import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiCheckCircle, FiUploadCloud, FiXCircle, FiArrowRight } from 'react-icons/fi';
import './Kycpage.css';
import { MyContext } from '../../../../Context/MyContext';
import { API_URL } from '../../../../Api/api';
import { getAuthToken } from '../../../../Api/authToken';
import {
  getUserKycForRole,
  getPendingListingFlow,
  resumePendingListingFlow,
} from '../../../../utils/userKyc';

const getStatusMeta = (status) => {
  if (status === 'approved') return { cls: 'kyc-status-approved', icon: <FiCheckCircle />, label: 'Approved' };
  if (status === 'rejected') return { cls: 'kyc-status-rejected', icon: <FiXCircle />, label: 'Rejected' };
  if (status === 'pending') return { cls: 'kyc-status-pending', icon: <FiAlertTriangle />, label: 'Pending Review' };
  return { cls: 'kyc-status-none', icon: <FiAlertTriangle />, label: 'Not Submitted' };
};

const KycPage = ({ setActiveView, initialRole = 'owner' }) => {
  const { roles } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRole, setSelectedRole] = useState(initialRole || location.state?.kycRole || 'owner');
  const [selectedDocTypeId, setSelectedDocTypeId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycData, setKycData] = useState(null);

  const listingRoles = useMemo(
    () => (Array.isArray(roles) ? roles : []).map((r) => ({ id: r.id, label: r.property_label || r.id })),
    [roles],
  );

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const availableDocs = kycData?.required_documents || [];
  const selectedDoc = availableDocs.find((d) => String(d.id) === String(selectedDocTypeId));

  const loadKyc = useCallback(async (role) => {
    setLoading(true);
    try {
      const data = await getUserKycForRole(role);
      setKycData(data);
      setSelectedFile(null);

      const docs = data?.required_documents || [];
      const latestDocId = data?.latest_submission?.documents?.[0]?.document_type_id;
      const defaultDocId = latestDocId && docs.some((d) => d.id === latestDocId)
        ? String(latestDocId)
        : docs[0]?.id ? String(docs[0].id) : '';
      setSelectedDocTypeId(defaultDocId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load KYC details');
      setKycData(null);
      setSelectedDocTypeId('');
      setSelectedFile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialRole) setSelectedRole(initialRole);
  }, [initialRole]);

  useEffect(() => {
    if (location.state?.kycRole) {
      setSelectedRole(location.state.kycRole);
    }
  }, [location.state?.kycRole]);

  useEffect(() => {
    if (selectedRole) loadKyc(selectedRole);
  }, [selectedRole, loadKyc]);

  const status = kycData?.kyc_status || 'not_submitted';
  const statusMeta = getStatusMeta(status);
  const kycRequired = Boolean(kycData?.kyc_required);
  const pendingFlow = getPendingListingFlow();
  const canResumeListing = status === 'approved' && Boolean(pendingFlow);

  const handleResumeListing = () => {
    if (resumePendingListingFlow(navigate)) return;
    if (setActiveView) setActiveView('content');
    else navigate('/account');
  };

  const handleDocTypeChange = (docId) => {
    setSelectedDocTypeId(docId);
    setSelectedFile(null);
  };

  const handleFileChange = (file) => {
    setSelectedFile(file || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!kycRequired) return toast.info('KYC is not required for this role.');
    if (status === 'pending') return toast.info('Your KYC is already under review.');
    if (status === 'approved') return toast.info('Your KYC is already approved.');
    if (!selectedDocTypeId) return toast.error('Please select a document type.');
    if (!selectedFile) return toast.error('Please upload the selected document.');

    if (!/\.(pdf|png|jpe?g|webp)$/i.test(selectedFile.name)) {
      return toast.error('Only PDF or image files are allowed');
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', selectedFile);
      const uploadRes = await axios.post(`${API_URL}/upload/single`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      const fileName = uploadRes.data?.path?.split('/').pop() || uploadRes.data?.filename;

      await axios.post(`${API_URL}/user-kyc/submit`, {
        listing_role: selectedRole,
        documents: [{ document_type_id: Number(selectedDocTypeId), document_file: fileName }],
      }, { headers: authHeaders });

      toast.success('KYC submitted successfully');
      await loadKyc(selectedRole);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit KYC');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kyc-page">
      <button type="button" className="kyc-back-btn" onClick={() => (setActiveView ? setActiveView('content') : navigate('/account'))}>
        ← Back to Profile
      </button>

      <div className="kyc-header">
        <h2>KYC Verification</h2>
        <p>Select one document type and upload it to complete verification for your listing role.</p>
      </div>

      <div className="kyc-group">
        <label htmlFor="kyc-role">Listing Role</label>
        <select
          id="kyc-role"
          className="kyc-select"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          {listingRoles.map((r) => (
            <option key={r.id} value={r.id}>{r.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="kyc-loading">Loading KYC details…</div>
      ) : !kycRequired ? (
        <div className="kyc-info-box">
          KYC is not currently required for the <strong>{kycData?.role_label || selectedRole}</strong> role.
        </div>
      ) : (
        <>
          <div className={`kyc-status-banner ${statusMeta.cls}`}>
            <span className="kyc-status-icon">{statusMeta.icon}</span>
            <div>
              <strong>{statusMeta.label}</strong>
              {status === 'pending' && (
                <p>
                  Your {kycData?.latest_submission?.documents?.[0]?.document_type_name || 'document'} is being reviewed.
                </p>
              )}
              {status === 'approved' && <p>You can submit properties as {kycData?.role_label || selectedRole}.</p>}
              {canResumeListing && (
                <button type="button" className="kyc-resume-btn" onClick={handleResumeListing}>
                  Continue Property Submission <FiArrowRight />
                </button>
              )}
              {status === 'rejected' && (
                <p>{kycData?.latest_submission?.admin_note || 'Please select a document type and upload again.'}</p>
              )}
              {status === 'not_submitted' && (
                <p>Select one document type from the list below and upload it to continue.</p>
              )}
            </div>
          </div>

          {(status === 'not_submitted' || status === 'rejected') && (
            <form className="kyc-form" onSubmit={handleSubmit}>
              {availableDocs.length === 0 ? (
                <div className="kyc-info-box">
                  No document types are configured for this role. Please contact the administrator.
                </div>
              ) : (
                <>
                  <div className="kyc-group">
                    <label htmlFor="kyc-doc-type">Document Type</label>
                    <select
                      id="kyc-doc-type"
                      className="kyc-select"
                      value={selectedDocTypeId}
                      onChange={(e) => handleDocTypeChange(e.target.value)}
                      required
                    >
                      <option value="">Select document type</option>
                      {availableDocs.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.name}
                        </option>
                      ))}
                    </select>
                    {selectedDoc?.description && (
                      <p className="kyc-doc-hint">{selectedDoc.description}</p>
                    )}
                  </div>

                  {selectedDocTypeId && (
                    <div className="kyc-group">
                      <label htmlFor="kyc-file">
                        Upload {selectedDoc?.name || 'Document'}
                      </label>
                      <input
                        id="kyc-file"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf,.webp"
                        onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                        required
                      />
                      {selectedFile && (
                        <p className="kyc-file-name">Selected: {selectedFile.name}</p>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="kyc-submit-btn"
                    disabled={submitting || !selectedDocTypeId || !selectedFile}
                  >
                    <FiUploadCloud /> {submitting ? 'Submitting…' : 'Submit KYC Document'}
                  </button>
                </>
              )}
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default KycPage;
